import React, { useState, useRef, useEffect } from 'react';
import { Download, Sparkles, FileText, ArrowRight, Lightbulb, Upload, HelpCircle, Settings, Plus, Minus, Save, RefreshCw, Maximize2, X } from 'lucide-react';
import { useAssessment } from './SharedContext';
import gradingPolicyService from '../services/gradingPolicyService';
import { useGradingPolicies } from '../hooks/useGradingPolicies';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import Image from '@tiptap/extension-image';

// TipTap MenuBar (from RubricCreator.js)
const MenuBar = ({ editor }) => {
  if (!editor) {
    return null;
  }

  const setLink = () => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const addImage = () => {
    const url = window.prompt('Image URL');
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  return (
    <div className="p-2 border-b border-gray-300 bg-gray-50 flex items-center flex-wrap gap-x-2 gap-y-1 text-sm">
      <button title="Undo" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()}>Undo</button>
      <button title="Redo" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()}>Redo</button>
      <div className="w-[1px] h-5 bg-gray-300 mx-1" />
      <button onClick={() => editor.chain().focus().toggleBold().run()} className={editor.isActive('bold') ? 'is-active' : ''} title="Bold"><b>B</b></button>
      <button onClick={() => editor.chain().focus().toggleItalic().run()} className={editor.isActive('italic') ? 'is-active' : ''} title="Italic"><i>I</i></button>
      <button onClick={() => editor.chain().focus().toggleUnderline().run()} className={editor.isActive('underline') ? 'is-active' : ''} title="Underline"><u>U</u></button>
      <button onClick={() => editor.chain().focus().toggleStrike().run()} className={editor.isActive('strike') ? 'is-active' : ''} title="Strikethrough"><s>S</s></button>
      <div className="w-[1px] h-5 bg-gray-300 mx-1" />
      <button onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} className={editor.isActive('heading', { level: 1 }) ? 'is-active' : ''} title="Heading 1">H1</button>
      <button onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className={editor.isActive('heading', { level: 2 }) ? 'is-active' : ''} title="Heading 2">H2</button>
      <button onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} className={editor.isActive('heading', { level: 3 }) ? 'is-active' : ''} title="Heading 3">H3</button>
      <div className="w-[1px] h-5 bg-gray-300 mx-1" />
      <button onClick={() => editor.chain().focus().toggleBulletList().run()} className={editor.isActive('bulletList') ? 'is-active' : ''} title="Bullet List">• List</button>
      <button onClick={() => editor.chain().focus().toggleOrderedList().run()} className={editor.isActive('orderedList') ? 'is-active' : ''} title="Numbered List">1. List</button>
      <div className="w-[1px] h-5 bg-gray-300 mx-1" />
      <button onClick={() => editor.chain().focus().setTextAlign('left').run()} className={editor.isActive({ textAlign: 'left' }) ? 'is-active' : ''} title="Align Left">Left</button>
      <button onClick={() => editor.chain().focus().setTextAlign('center').run()} className={editor.isActive({ textAlign: 'center' }) ? 'is-active' : ''} title="Align Center">Center</button>
      <button onClick={() => editor.chain().focus().setTextAlign('right').run()} className={editor.isActive({ textAlign: 'right' }) ? 'is-active' : ''} title="Align Right">Right</button>
      <div className="w-[1px] h-5 bg-gray-300 mx-1" />
      <button onClick={setLink} className={editor.isActive('link') ? 'is-active' : ''} title="Add Link">Link</button>
      <button onClick={addImage} title="Add Image">Image</button>
      <style jsx>{`
                button { padding: 0.25rem 0.5rem; border-radius: 4px; transition: background-color 0.2s; }
                button:hover { background-color: #e5e7eb; }
                button.is-active { background-color: #dbeafe; color: #1d4ed8; font-weight: bold; }
                button:disabled { opacity: 0.5; cursor: not-allowed; }
            `}</style>
    </div>
  );
};

// TiptapEditor component (from RubricCreator.js)
const TiptapEditor = React.forwardRef(({ value, onChange, placeholder }, ref) => {
  const editor = useEditor({
    extensions: [StarterKit, Underline, Image, Link.configure({ openOnClick: false }), TextAlign.configure({ types: ['heading', 'paragraph'] })],
    content: value || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'min-h-[200px] p-4 focus:outline-none',
      },
    },
  });

  React.useImperativeHandle(ref, () => editor, [editor]);

  useEffect(() => {
    if (editor && !editor.isDestroyed) {
      const isSame = editor.getHTML() === value;
      if (!isSame) {
        editor.commands.setContent(value, false);
      }
    }
  }, [value, editor]);

  return (
    <div className="rich-text-editor border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 transition-all duration-200">
      <MenuBar editor={editor} />
      <EditorContent editor={editor} placeholder={placeholder} />
      <style>{`
                .ProseMirror p.is-editor-empty:first-child::before { content: attr(data-placeholder); float: left; color: #adb5bd; pointer-events: none; height: 0; }
                .ProseMirror h1 { font-size: 1.75em; font-weight: bold; }
                .ProseMirror h2 { font-size: 1.5em; font-weight: bold; }
                .ProseMirror h3 { font-size: 1.25em; font-weight: bold; }
                .ProseMirror ul { list-style-type: disc; padding-left: 2rem; }
                .ProseMirror ol { list-style-type: decimal; padding-left: 2rem; }
                .ProseMirror img { max-width: 100%; height: auto; display: block; margin-left: auto; margin-right: auto; }
                .ProseMirror a { color: #2563eb; text-decoration: underline; cursor: pointer; }
            `}</style>
    </div>
  );
});
TiptapEditor.displayName = 'TiptapEditor';


const AIRubricPromptGenerator = () => {
  const {
    aiPromptFormData,
    updateAIPromptFormData,
    initializeAIPromptFormData,
    assignmentPromptFormData
  } = useAssessment();

  const { data: policies = [] } = useGradingPolicies();
  const supportedProgramTypes = gradingPolicyService.getSupportedProgramTypes();
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!aiPromptFormData) {
      initializeAIPromptFormData();
    }
  }, [aiPromptFormData, initializeAIPromptFormData]);

  const [generatedPrompt, setGeneratedPrompt] = useState('');
  const [showPrompt, setShowPrompt] = useState(false);
  const userEditedCriteriaCount = useRef(false);

  // Auto-detect user-provided criteria count from Assignment Details page
  useEffect(() => {
    if (userEditedCriteriaCount.current) return; // User has manually edited, don't re-sync
    if (!assignmentPromptFormData?.gradingCriteria) return;

    const rawCriteria = assignmentPromptFormData.gradingCriteria;
    if (!rawCriteria || rawCriteria.trim() === '' || rawCriteria.trim() === '<p></p>') return;

    // Strip HTML tags to get plain text for counting
    const plainText = rawCriteria
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>/gi, '\n')
      .replace(/<\/li>/gi, '\n')
      .replace(/<[^>]*>/g, '')
      .trim();

    if (!plainText) return;

    // Count non-empty lines as criteria
    const lines = plainText.split('\n').filter(line => line.trim().length > 0);
    const detectedCount = lines.length;

    if (detectedCount >= 1 && detectedCount <= 8) {
      updateAIPromptFormData('numCriteria', detectedCount.toString());
    }
  }, [assignmentPromptFormData?.gradingCriteria, updateAIPromptFormData]);

  const [inlineEditor, setInlineEditor] = useState({ show: false, content: '', field: null, onSave: null });
  const richTextContentRef = useRef('');
  const editorRef = useRef(null);

  const handleInputChange = (field, value) => {
    updateAIPromptFormData(field, value);

    if (field === 'numCriteria') {
      userEditedCriteriaCount.current = true;
    }

    if (field === 'programType') {
      const selectedPolicy = policies.find(p => p.program === value);
      if (selectedPolicy && selectedPolicy.passingGrade) {
        updateAIPromptFormData('passingThreshold', selectedPolicy.passingGrade.toString());
      } else {
        updateAIPromptFormData('passingThreshold', value === 'Degree Programs' ? '60' : '55');
      }
    }
  };

  const importAssignmentData = () => {
    if (!assignmentPromptFormData) {
      alert('No assignment data found to import. Please create an assignment first.');
      return;
    }

    let newCriteriaType = 'ai-suggested';
    let newUserCriteria = '';
    if (assignmentPromptFormData.gradingCriteria && assignmentPromptFormData.gradingCriteria.trim() !== '' && assignmentPromptFormData.gradingCriteria.trim() !== '<p></p>') {
      newCriteriaType = 'user-provided';
      newUserCriteria = assignmentPromptFormData.gradingCriteria;
    }

    const mappedData = {
      assignmentType: assignmentPromptFormData.assignmentTitle?.trim() || 'Assignment',
      programLevel: assignmentPromptFormData.programLevel?.trim() || 'Semester 1',
      subjectArea: assignmentPromptFormData.subjectArea?.trim() || 'General Studies',
      assignmentDescription: assignmentPromptFormData.assignmentDescription?.trim() || 'Assignment description imported from assignment prompt.',
      rationale: assignmentPromptFormData.rationale?.trim() || '',
      weightPercentage: assignmentPromptFormData.weightPercentage || '',
      totalPoints: assignmentPromptFormData.weightPercentage ? (parseInt(assignmentPromptFormData.weightPercentage) * 4).toString() : '100',
      passingThreshold: aiPromptFormData.passingThreshold || '55',
      learningObjectives: Array.isArray(assignmentPromptFormData.clos) ? assignmentPromptFormData.clos.filter(clo => clo.text && clo.text.trim()).map(clo => ({ id: clo.id || Date.now() + Math.random(), number: clo.number, text: clo.text, type: clo.type })) : [],
      specialConsiderations: assignmentPromptFormData.specialInstructions?.trim() || '',
      criteriaType: newCriteriaType,
      userCriteria: newUserCriteria,
      numCriteria: aiPromptFormData.numCriteria || '5',
      timeFrameUnit: aiPromptFormData.timeFrameUnit || 'weeks',
      studentPopulation: aiPromptFormData.studentPopulation || 'Mixed Ability',
      timeFrameNumber: aiPromptFormData.timeFrameNumber || '',
      achievementLevels: aiPromptFormData.achievementLevels || '7'
    };

    Object.entries(mappedData).forEach(([field, value]) => {
      updateAIPromptFormData(field, value);
    });

    alert('Assignment data imported successfully! Review and adjust as needed.');
  };

  const formData = aiPromptFormData ? {
    ...aiPromptFormData,
    achievementLevels: aiPromptFormData.achievementLevels || '7',
    passingThreshold: aiPromptFormData.passingThreshold || '55'
  } : {
    assignmentType: '', programType: '', programLevel: 'Semester 1', subjectArea: '', assignmentDescription: '',
    rationale: '', totalPoints: '100', weightPercentage: '', passingThreshold: '55',
    numCriteria: '5', criteriaType: 'ai-suggested', userCriteria: '',
    learningObjectives: [{ id: 1, number: '1', text: '', type: 'CLO' }, { id: 2, number: '2', text: '', type: 'CLO' }, { id: 3, number: '3', text: '', type: 'CLO' }, { id: 4, number: '4', text: '', type: 'CLO' }],
    studentPopulation: '', timeFrameNumber: '', timeFrameUnit: 'weeks',
    specialConsiderations: '', achievementLevels: '7'
  };

  useEffect(() => { }, [aiPromptFormData]);

  const levelTemplates = {
    7: [
      { level: 'incomplete', name: 'Incomplete', multiplier: 0, description: 'No submission or unusable', overallComment: "The work is missing or unusable—core requirements were not submitted or cannot be assessed. Please review the brief and resubmit when the essential elements are complete." },
      { level: 'unacceptable', name: 'Unacceptable', multiplier: 0.3, description: 'Below minimum standards', overallComment: "Key criteria were not met and the fundamentals are largely absent. Revisit the assignment directions and foundational principles before attempting a full redo." },
      { level: 'developing', name: 'Developing (PASS)', multiplier: 0.55, description: 'Meets minimum passing requirements', overallComment: "The submission shows some understanding of the task, but execution is inconsistent. Strengthen the basics—timing, planning, and technical setup—to move forward." },
      { level: 'acceptable', name: 'Acceptable', multiplier: 0.7, description: 'Acceptable work, exceeds minimum pass', overallComment: "All required components are present and meet the baseline standard. Continue refining the core principles and clean up minor technical issues to elevate the work." },
      { level: 'emerging', name: 'Emerging', multiplier: 0.82, description: 'Above standard expectations', overallComment: "Solid grasp of the fundamentals and growing stylistic control. Focus on polishing details, smoothing transitions, and tightening accuracy to reach higher performance levels." },
      { level: 'accomplished', name: 'Accomplished', multiplier: 0.92, description: 'Strong professional quality', overallComment: "Confident, well-executed work that clearly demonstrates mastery of the principles. Only minor tweaks remain—fine-tune nuance and consistency for an exceptional finish." },
      { level: 'exceptional', name: 'Exceptional', multiplier: 1.0, description: 'Outstanding professional quality', overallComment: "Outstanding, professional-quality result that exceeds expectations in creativity, clarity, and technical polish. This piece sets a benchmark for excellence. Great job!" }
    ]
  };

  const generateAchievementLevelsText = () => {
    const selectedLevels = parseInt(formData.achievementLevels) || 7;
    const template = levelTemplates[selectedLevels] || levelTemplates[7];
    return template.map((level, index) => `  ${index + 1}. ${level.name} (${level.multiplier}x multiplier)`).join('\n');
  };

  const generateRubricLevelsJSON = () => {
    const selectedLevels = parseInt(formData.achievementLevels) || 7;
    const template = levelTemplates[selectedLevels] || levelTemplates[7];
    const colors = ['#95a5a6', '#e74c3c', '#f39c12', '#27ae60', '#2980b9', '#16a085', '#8e44ad'];
    return template.map((level, index) => `    {
      "level": "${level.level}",
      "name": "${level.name}",
      "multiplier": ${level.multiplier},
      "color": "${colors[index] || '#95a5a6'}",
      "description": "${level.description}",
      "overallComment": "${level.overallComment.replace(/"/g, '\\"')}"
    }`).join(',\n');
  };

  const generateOverallAchievementLevels = () => {
    const selectedLevels = parseInt(formData.achievementLevels) || 7;
    const template = levelTemplates[selectedLevels] || levelTemplates[7];
    const totalPoints = parseInt(formData.totalPoints) || 100;
    return template.map((level, index) => {
      const points = Math.floor(totalPoints * level.multiplier);
      const percentage = Math.floor(level.multiplier * 100);
      return `    {
      "level": "${level.level}",
      "name": "${level.name}",
      "multiplier": ${level.multiplier},
      "color": "${['#95a5a6', '#e74c3c', '#f39c12', '#27ae60', '#2980b9', '#16a085', '#8e44ad'][index] || '#95a5a6'}",
      "description": "${level.overallComment.replace(/"/g, '\\"')}",
      "pointThreshold": ${points},
      "percentageThreshold": ${percentage}
    }`;
    }).join(',\n');
  };

  const addCLO = () => {
    const currentCLOs = formData.learningObjectives || [];
    if (currentCLOs.length < 15) {
      const newCLO = { id: Date.now(), number: (currentCLOs.length + 1).toString(), text: '', type: 'CLO' };
      handleInputChange('learningObjectives', [...currentCLOs, newCLO]);
    }
  };

  const removeCLO = (cloId) => {
    const currentCLOs = formData.learningObjectives || [];
    if (currentCLOs.length > 1) {
      handleInputChange('learningObjectives', currentCLOs.filter(clo => clo.id !== cloId));
    }
  };

  const updateCLO = (cloId, field, value) => {
    const currentCLOs = formData.learningObjectives || [];
    handleInputChange('learningObjectives', currentCLOs.map(clo => clo.id === cloId ? { ...clo, [field]: value } : clo));
  };

  const generateNumberOptions = () => {
    const options = [];
    for (let i = 1; i <= 15; i++) {
      options.push(<option key={i} value={i.toString()}>{i}</option>);
      for (let j = 1; j <= 5; j++) {
        const decimal = `${i}.${j}`;
        options.push(<option key={decimal} value={decimal}>{decimal}</option>);
      }
    }
    return options;
  };

  const renderFormattedContent = (htmlContent) => {
    if (!htmlContent) return '';
    let textContent = htmlContent
      .replace(/<br\s*\/?>/gi, '\n').replace(/<\/p>/gi, '\n').replace(/<p[^>]*>/gi, '')
      .replace(/<strong[^>]*>(.*?)<\/strong>/gi, '**$1**').replace(/<b[^>]*>(.*?)<\/b>/gi, '**$1**')
      .replace(/<em[^>]*>(.*?)<\/em>/gi, '*$1*').replace(/<i[^>]*>(.*?)<\/i>/gi, '*$1*')
      .replace(/<u[^>]*>(.*?)<\/u>/gi, '_$1_').replace(/<h3[^>]*>(.*?)<\/h3>/gi, '### $1\n')
      .replace(/<li[^>]*>(.*?)<\/li>/gi, '• $1\n').replace(/<\/ul>/gi, '\n').replace(/<ul[^>]*>/gi, '')
      .replace(/<\/ol>/gi, '\n').replace(/<ol[^>]*>/gi, '').replace(/<[^>]*>/g, '')
      .replace(/\n\s*\n/g, '\n').trim();
    return textContent;
  };

  const convertFormattedTextToHtml = (text) => {
    if (!text) return '';
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/_(.*?)_/g, '<u>$1</u>')
      .replace(/### (.*?)\n/g, '<h3>$1</h3>')
      .replace(/• (.*?)\n/g, '<li>$1</li>')
      .replace(/\n/g, '<br>')
      .replace(/(<li>.*<\/li>)/g, '<ul>$1</ul>');
  };

  const openInlineEditor = (content, field, onSave) => {
    richTextContentRef.current = content || '';
    setInlineEditor({ show: true, content: content || '', field, onSave });
  };

  const closeInlineEditor = (save = false) => {
    if (save && inlineEditor.onSave) {
      inlineEditor.onSave(richTextContentRef.current);
    }
    setInlineEditor({ show: false, content: '', field: null, onSave: null });
    richTextContentRef.current = '';
  };

  const generatePrompt = () => {
    const courseLevel = `${formData.programType} - ${formData.programLevel}`;
    const criteriaCount = formData.numCriteria || '5';
    const timeFrame = formData.timeFrameNumber ? `${formData.timeFrameNumber} ${formData.timeFrameUnit}` : 'Standard assignment duration';
    const criteriaInstructions = formData.criteriaType === 'user-provided' && formData.userCriteria ? `\n**User-Specified Criteria to Include:**\n${renderFormattedContent(formData.userCriteria)}\n\nPlease use these criteria as the basis for your rubric. You may refine the names and add detailed descriptions, but ensure all listed criteria are included.` : `\n**Criteria Generation:**\nPlease suggest ${criteriaCount} appropriate criteria for this ${formData.subjectArea} assignment. Base your suggestions on best practices for ${formData.assignmentType} assessment and the specified learning context.`;
    const selectedLevels = parseInt(formData.achievementLevels) || 7;
    const template = levelTemplates[selectedLevels] || levelTemplates[7];
    const achievementLevelsText = generateAchievementLevelsText();
    const rubricLevelsJSON = generateRubricLevelsJSON();
    const overallAchievementLevels = generateOverallAchievementLevels();
    const learningObjectivesText = Array.isArray(formData.learningObjectives) ? formData.learningObjectives.filter(clo => clo.text && clo.text.trim()).map(clo => `${clo.type}${clo.number}: ${clo.text}`).join('\n') : formData.learningObjectives || 'To be determined based on assignment type';
    const rationaleText = formData.rationale ? `\n**Assignment Rationale:** ${renderFormattedContent(formData.rationale)}` : '';
    const passingThresholdInstructions = `
**CRITICAL PASSING THRESHOLD REQUIREMENTS:**
- The passing threshold is set at ${formData.passingThreshold}%
- This means students need ${formData.passingThreshold}% of total points to pass the assignment
- When calculating point ranges for each achievement level, ensure that levels at or above ${formData.passingThreshold}% are clearly marked as PASSING
- Level descriptions must reflect whether each level represents passing or failing work
- The point calculations must align perfectly with the ${formData.passingThreshold}% threshold
- For levels below ${formData.passingThreshold}%, descriptions should indicate areas needing improvement to reach passing standard
- For levels at or above ${formData.passingThreshold}%, descriptions should indicate competency levels above the minimum passing requirement`;

    const prompt = `Create a comprehensive educational rubric for the following assignment:

**Assignment Type:** ${formData.assignmentType}
**Course Level:** ${courseLevel}
**Subject Area:** ${formData.subjectArea}
**Assignment Description:** ${renderFormattedContent(formData.assignmentDescription)}${rationaleText}${criteriaInstructions}

**Specific Requirements:**
- Total Points: ${formData.totalPoints} points
- Weight: ${formData.weightPercentage || 'TBD'}% of Final Grade
- Passing Threshold: ${formData.passingThreshold}% (minimum percentage required to pass)
- Number of Criteria: ${criteriaCount} main criteria
- Assessment Levels: Use this ${selectedLevels}-level system:
${achievementLevelsText}

${passingThresholdInstructions}

**Output Format Required:**
Please provide the output as a complete JSON file matching this exact structure:

\`\`\`json
{
  "assignmentInfo": {
    "title": "${formData.assignmentType}",
    "description": "${renderFormattedContent(formData.assignmentDescription).replace(/"/g, '\\"')}",
    "weight": ${formData.weightPercentage || 25},
    "totalPoints": ${formData.totalPoints},
    "passingThreshold": ${formData.passingThreshold},
    "dueDate": "2025-04-15",
    "courseCode": "COURSE_CODE",
    "instructor": "Instructor Name"
  },
  "rubricLevels": [
${rubricLevelsJSON}
  ],
  "overallAchievementLevels": [
${overallAchievementLevels}
  ],
  "criteria": [
    {
      "id": "criterion_id_1",
      "name": "Criterion Name 1",
      "description": "Detailed description of what this criterion assesses",
      "maxPoints": 25,
      "levels": {
${template.map(level => `        "${level.level}": {
          "pointRange": "Calculate appropriate range based on criterion maxPoints and level multiplier",
          "description": "Detailed description for this level that clearly indicates if this represents passing or failing work based on the ${formData.passingThreshold}% threshold"
        }`).join(',\n')}
      },
      "feedbackLibrary": {
          "strengths": ["Strong example feedback for good performance", "Another positive feedback option", "Excellent demonstration of understanding", "Shows mastery of key concepts"],
          "improvements": ["Suggestion for improvement", "Another improvement suggestion", "Consider strengthening this area", "Focus on developing this skill further"],
          "general": ["General feedback comment", "Overall observation about the work", "Additional contextual feedback", "Note about assignment completion"]
        }
    }
  ]
}
\`\`\`

**Important Notes:**
${formData.criteriaType === 'user-provided' ? `**NOTE:** The user has provided specific criteria. Ensure all listed criteria are included and refined with professional descriptions appropriate for ${formData.subjectArea} assessment.` : `**NOTE:** Generate appropriate criteria based on best practices for ${formData.assignmentType} assessment in ${formData.subjectArea}.`}

**CRITICAL FORMAT REQUIREMENTS:**
- Use descriptive IDs like "animation_principles", "technical_execution" for criterion IDs
- The "levels" object must contain objects with "pointRange" and "description" properties
- Calculate appropriate point ranges for each level based on the criterion's maxPoints and achievement level multipliers
- Each level (${template.map(l => l.level).join(', ')}) should be an object with pointRange and description
- IMPORTANT: All point values in every "pointRange" field must be whole numbers — no decimals. Round to the nearest whole number.
- IMPORTANT: The "Unacceptable" level must never be assigned only 1 point regardless of the criterion's maxPoints. It should receive a meaningful low score (e.g., approximately 30% of maxPoints). For example, a 20-point criterion should show an Unacceptable value of around 6 points, not 1.
- IMPORTANT: Include all three feedback categories in feedbackLibrary: "strengths", "improvements", and "general"
- Provide 3-5 sample feedback comments for each category to give instructors a comprehensive starting point
- The "general" category should include neutral observations and contextual comments
- Ensure the JSON is valid and complete - test it in a JSON validator before providing
- The output must be importable directly into the Rubric Builder tool

**LEVEL DESCRIPTION QUALITY REQUIREMENTS:**
- Each achievement level description for every criterion MUST be:
  1. Detailed and specific to the subject matter of that criterion (not generic or placeholder language)
  2. Clearly describe what performance at that level looks like, not just the outcome
  3. Focus on observable qualities, execution, consistency, and quality relevant to the criterion
  4. Avoid unnecessary repetition across levels while maintaining clear progression from lowest to highest
- MAXIMUM LENGTH: Each individual level description must be no more than 450 characters
- These limits apply per level, not per criterion
- Do NOT use vague phrases like "meets expectations" or "needs improvement" without specifying what is expected or what needs improving in the context of the criterion

**PASSING THRESHOLD INTEGRATION REQUIREMENTS:**
- The ${formData.passingThreshold}% passing threshold MUST be clearly reflected in:
  1. Point range calculations for each criterion level
  2. Level descriptions that explicitly state whether the level represents passing or failing work
  3. The overallAchievementLevels section with accurate point thresholds and passing/failing indicators
  4. Feedback library comments that reference passing standards where appropriate
- Ensure that students and instructors can clearly identify what constitutes passing vs. failing work at each level
- All point calculations must mathematically align with the specified passing threshold

**Additional Context:**
- Learning objectives: ${learningObjectivesText}
- Student population: ${formData.studentPopulation || 'Mixed ability'}
- Time frame: ${timeFrame}
- Special considerations: ${renderFormattedContent(formData.specialConsiderations) || 'None specified'}

Please generate a complete, ready-to-import, downloadable JSON file that matches the structure exactly and can be directly imported into the Rubric Builder tool. Ensure that each criterion includes comprehensive feedback libraries with:
- **Strengths comments**: Positive feedback for good performance
- **Improvements comments**: Constructive suggestions for enhancement  
- **General comments**: Neutral observations and contextual comments

All three feedback categories (strengths, improvements, general) are required for the rubric to function properly in the assessment tool.

**FINAL VERIFICATION CHECKLIST:**
- ✓ Passing threshold of ${formData.passingThreshold}% is clearly reflected in all point calculations
- ✓ Level descriptions explicitly indicate passing vs. failing status
- ✓ overallAchievementLevels section includes accurate point and percentage thresholds
- ✓ Point ranges for each criterion level align with the overall passing threshold
- ✓ All point values are whole numbers — no decimal points anywhere in the output
- ✓ The Unacceptable level has a meaningful point value (approx. 30% of each criterion's maxPoints), never just 1 point
- ✓ JSON structure is complete and valid for direct import into Rubric Builder`;

    setGeneratedPrompt(prompt);
    setShowPrompt(true);
  };

  const exportPrompt = () => {
    const element = document.createElement('a');
    const file = new Blob([generatedPrompt], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `ai-rubric-prompt-${formData.assignmentType.replace(/\s+/g, '-').toLowerCase()}.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedPrompt);
    alert('Prompt copied to clipboard!');
  };

  const isFormValid = formData.assignmentType?.trim() && formData.programType?.trim() &&
    formData.programLevel?.trim() && formData.subjectArea?.trim() && formData.assignmentDescription?.trim() &&
    (formData.criteriaType === 'ai-suggested' || (formData.criteriaType === 'user-provided' && formData.userCriteria?.trim())) &&
    (Array.isArray(formData.learningObjectives) ? formData.learningObjectives.some(clo => clo.text?.trim()) : formData.learningObjectives?.trim());

  const exportFormDataAsJSON = () => {
    const dataToExport = { ...formData, exportDate: new Date().toISOString(), version: '1.0', type: 'ai-rubric-prompt-data' };
    const element = document.createElement('a');
    const file = new Blob([JSON.stringify(dataToExport, null, 2)], { type: 'application/json' });
    element.href = URL.createObjectURL(file);
    element.download = `ai-rubric-prompt-data-${formData.assignmentType?.replace(/\s+/g, '-').toLowerCase() || 'untitled'}-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const importFormDataFromJSON = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const importedData = JSON.parse(e.target.result);
          if (importedData.type !== 'ai-rubric-prompt-data') {
            alert('Invalid file type. Please select an AI Rubric Prompt data file.');
            return;
          }
          Object.entries(importedData).forEach(([field, value]) => {
            if (field !== 'exportDate' && field !== 'version' && field !== 'type') {
              updateAIPromptFormData(field, value);
            }
          });
          alert(`Form data imported successfully!${importedData.exportDate ? `\nExported: ${new Date(importedData.exportDate).toLocaleDateString()}` : ''}`);
        } catch (error) {
          alert('Error importing file: Please ensure it\'s a valid JSON file.');
        }
      };
      reader.readAsText(file);
    }
    event.target.value = '';
  };

  const triggerJSONImport = () => {
    fileInputRef.current?.click();
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset all fields to their default values?')) {
      initializeAIPromptFormData();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white shadow-lg rounded-lg">
          <div className="px-6 py-8">
            <div className="text-center mb-8 relative">
              <h1 className="text-4xl font-bold text-gray-900 mb-4 flex items-center justify-center gap-3">
                <Sparkles className="text-yellow-500" size={40} />
                AI Rubric Prompt Generator
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Generate comprehensive prompts for AI tools to create professional educational rubrics
              </p>
              <button
                onClick={handleReset}
                className="absolute top-0 right-0 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 text-sm font-medium"
                title="Reset all form fields to default"
              >
                <RefreshCw className="w-4 h-4" />
                Reset to Default
              </button>
            </div>

            <div className="mb-6 flex justify-center gap-4">
              <button onClick={triggerJSONImport} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2">
                <Upload className="w-4 h-4" />
                Import JSON Data
              </button>
              <button onClick={exportFormDataAsJSON} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2">
                <Save className="w-4 h-4" />
                Export JSON Data
              </button>
              <input ref={fileInputRef} type="file" accept=".json" onChange={importFormDataFromJSON} className="hidden" />
            </div>

            {!showPrompt ? (
              <>
                {assignmentPromptFormData && (
                  <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-blue-800">Import Assignment Data</h3>
                        <p className="text-blue-600 text-sm">
                          Assignment detected: "{assignmentPromptFormData.assignmentTitle || 'Untitled'}"
                        </p>
                      </div>
                      <button onClick={importAssignmentData} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                        Import Data
                      </button>
                    </div>
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-8">
                  <div className="space-y-6">
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-200">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <FileText className="text-blue-600" size={20} />
                        Assignment Information
                      </h3>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Assignment Type *</label>
                          <input type="text" value={formData.assignmentType} onChange={(e) => handleInputChange('assignmentType', e.target.value)} placeholder="e.g., Portfolio Project, Research Paper, Presentation" className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Program Type *</label>
                            <select value={formData.programType} onChange={(e) => handleInputChange('programType', e.target.value)} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                              <option value="">Select Type</option>
                              {supportedProgramTypes.map(type => (<option key={type.value} value={type.label}>{type.label}</option>))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Program Level *</label>
                            <select value={formData.programLevel} onChange={(e) => handleInputChange('programLevel', e.target.value)} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                              <option value="">Select Level</option>
                              <option value="Semester 1">Semester 1</option>
                              <option value="Semester 2">Semester 2</option>
                              <option value="Semester 3">Semester 3</option>
                              <option value="Semester 4">Semester 4</option>
                              <option value="Semester 5">Semester 5</option>
                              <option value="Semester 6">Semester 6</option>
                              <option value="Year 1">Year 1</option>
                              <option value="Year 2">Year 2</option>
                              <option value="Year 3">Year 3</option>
                              <option value="Year 4">Year 4</option>
                            </select>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Subject Area *</label>
                          <input type="text" value={formData.subjectArea} onChange={(e) => handleInputChange('subjectArea', e.target.value)} placeholder="e.g., Digital Media, Business Administration, Web Development" className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Assignment Description *</label>
                          <div className="relative">
                            <textarea value={renderFormattedContent(formData.assignmentDescription)} onChange={(e) => handleInputChange('assignmentDescription', convertFormattedTextToHtml(e.target.value))} placeholder="Detailed description of what students need to accomplish..." className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent h-24 resize-y" />
                            <button type="button" onClick={() => openInlineEditor(formData.assignmentDescription, 'Assignment Description', (newContent) => handleInputChange('assignmentDescription', newContent))} className="absolute top-2 right-2 p-1 bg-blue-500 text-white rounded hover:bg-blue-600">
                              <Maximize2 size={14} />
                            </button>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Assignment Rationale</label>
                          <div className="relative">
                            <textarea value={renderFormattedContent(formData.rationale)} onChange={(e) => handleInputChange('rationale', convertFormattedTextToHtml(e.target.value))} placeholder="Why this assignment is important and what it evaluates..." className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent h-24 resize-y" />
                            <button type="button" onClick={() => openInlineEditor(formData.rationale, 'Assignment Rationale', (newContent) => handleInputChange('rationale', newContent))} className="absolute top-2 right-2 p-1 bg-blue-500 text-white rounded hover:bg-blue-600">
                              <Maximize2 size={14} />
                            </button>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Passing Threshold (%)</label>
                          <input type="number" min="0" max="100" value={formData.passingThreshold} onChange={(e) => handleInputChange('passingThreshold', e.target.value)} placeholder="55" className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                          <p className="text-xs text-gray-500 mt-1">e.g., 55 for Developing, 70 for Acceptable</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-lg border border-green-200">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <Settings className="text-green-600" size={20} />
                        Rubric Configuration
                      </h3>
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Total Points</label>
                            <input type="number" value={formData.totalPoints} onChange={(e) => handleInputChange('totalPoints', e.target.value)} placeholder="100" className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Weight (% of Final Grade)</label>
                            <input type="number" value={formData.weightPercentage} onChange={(e) => handleInputChange('weightPercentage', e.target.value)} placeholder="25" className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Number of Criteria</label>
                            <select value={formData.numCriteria} onChange={(e) => handleInputChange('numCriteria', e.target.value)} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                              <option value="3">3 Criteria</option>
                              <option value="4">4 Criteria</option>
                              <option value="5">5 Criteria</option>
                              <option value="6">6 Criteria</option>
                              <option value="7">7 Criteria</option>
                              <option value="8">8 Criteria</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Achievement Levels</label>
                            <select value={formData.achievementLevels} onChange={(e) => handleInputChange('achievementLevels', e.target.value)} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                              <option value="2">2 Levels (Simple)</option>
                              <option value="3">3 Levels (Basic)</option>
                              <option value="4">4 Levels (Standard)</option>
                              <option value="5">5 Levels (Detailed)</option>
                              <option value="6">6 Levels (Advanced)</option>
                              <option value="7">7 Levels (Professional)</option>
                            </select>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-3">Criteria Type</label>
                          <div className="space-y-3">
                            <label className="flex items-center cursor-pointer p-2 hover:bg-gray-50 rounded-lg transition-colors">
                              <input type="radio" name="criteriaType" value="ai-suggested" checked={formData.criteriaType === 'ai-suggested'} onChange={(e) => handleInputChange('criteriaType', e.target.value)} className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 focus:ring-2 mr-3" />
                              <span className="text-sm font-medium text-gray-900">AI-Suggested Criteria<span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">Recommended</span></span>
                            </label>
                            <label className="flex items-center cursor-pointer p-2 hover:bg-gray-50 rounded-lg transition-colors">
                              <input type="radio" name="criteriaType" value="user-provided" checked={formData.criteriaType === 'user-provided'} onChange={(e) => handleInputChange('criteriaType', e.target.value)} className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 focus:ring-2 mr-3" />
                              <span className="text-sm font-medium text-gray-900">User-Provided Criteria</span>
                            </label>
                          </div>
                        </div>
                        {formData.criteriaType === 'user-provided' && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Your Criteria *</label>
                            <div className="relative">
                              <textarea value={renderFormattedContent(formData.userCriteria)} onChange={(e) => handleInputChange('userCriteria', convertFormattedTextToHtml(e.target.value))} placeholder="List your criteria..." className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent h-24 resize-y" />
                              <button type="button" onClick={() => openInlineEditor(formData.userCriteria, 'Your Criteria', (newContent) => handleInputChange('userCriteria', newContent))} className="absolute top-2 right-2 p-1 bg-blue-500 text-white rounded hover:bg-blue-600">
                                <Maximize2 size={14} />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="space-y-6">
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-lg border border-purple-200">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <Lightbulb className="text-purple-600" size={20} />
                        Learning Context
                      </h3>
                      <div className="space-y-4">
                        <div>
                          <div className="flex items-center justify-between mb-4">
                            <label className="block text-sm font-medium text-gray-700">Learning Objectives</label>
                            <button type="button" onClick={addCLO} disabled={(formData.learningObjectives || []).length >= 15} className={`flex items-center gap-2 px-3 py-1 text-sm rounded-lg font-medium ${(formData.learningObjectives || []).length >= 15 ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-purple-600 text-white hover:bg-purple-700'}`}>
                              <Plus className="w-4 h-4" />
                              Add CLO
                            </button>
                          </div>
                          <div className="space-y-3">
                            {(formData.learningObjectives || []).map((clo) => (
                              <div key={clo.id} className="flex items-start gap-3 p-3 bg-white rounded border">
                                <div className="flex gap-2">
                                  <select value={clo.type} onChange={(e) => updateCLO(clo.id, 'type', e.target.value)} className="border rounded px-2 py-1 text-sm">
                                    <option value="CLO">CLO</option>
                                    <option value="ULO">ULO</option>
                                  </select>
                                  <select value={clo.number} onChange={(e) => updateCLO(clo.id, 'number', e.target.value)} className="w-16 border rounded px-2 py-1 text-sm">
                                    {generateNumberOptions()}
                                  </select>
                                </div>
                                <input type="text" value={clo.text} onChange={(e) => updateCLO(clo.id, 'text', e.target.value)} className="flex-1 border rounded px-3 py-1" placeholder="Enter learning outcome description..." />
                                <button type="button" onClick={() => removeCLO(clo.id)} disabled={(formData.learningObjectives || []).length <= 1} className={`p-1 rounded ${(formData.learningObjectives || []).length <= 1 ? 'text-gray-400 cursor-not-allowed' : 'text-red-600 hover:bg-red-50'}`}>
                                  <Minus className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Student Population</label>
                          <select value={formData.studentPopulation} onChange={(e) => handleInputChange('studentPopulation', e.target.value)} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                            <option value="">Select Population</option>
                            <option value="Beginner Level">Beginner Level</option>
                            <option value="Intermediate Level">Intermediate Level</option>
                            <option value="Advanced Level">Advanced Level</option>
                            <option value="Mixed Ability">Mixed Ability</option>
                            <option value="Adult Learners">Adult Learners</option>
                            <option value="International Students">International Students</option>
                          </select>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Time Frame</label>
                            <input type="number" value={formData.timeFrameNumber} onChange={(e) => handleInputChange('timeFrameNumber', e.target.value)} placeholder="2" className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Unit</label>
                            <select value={formData.timeFrameUnit} onChange={(e) => handleInputChange('timeFrameUnit', e.target.value)} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                              <option value="weeks">Weeks</option>
                              <option value="classes">Classes</option>
                              <option value="months">Months</option>
                              <option value="days">Days</option>
                            </select>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Special Considerations</label>
                          <div className="relative">
                            <textarea value={renderFormattedContent(formData.specialInstructions)} onChange={(e) => handleInputChange('specialInstructions', convertFormattedTextToHtml(e.target.value))} placeholder="Any special requirements, accommodations, or considerations..." className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent h-24 resize-y" />
                            <button type="button" onClick={() => openInlineEditor(formData.specialInstructions, 'Special Considerations', (newContent) => handleInputChange('specialInstructions', newContent))} className="absolute top-2 right-2 p-1 bg-blue-500 text-white rounded hover:bg-blue-600">
                              <Maximize2 size={14} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gradient-to-r from-orange-50 to-red-50 p-6 rounded-lg border border-orange-200">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <Settings className="text-orange-600" size={20} />
                        Achievement Levels Preview
                      </h3>
                      <div className="space-y-2">
                        {(levelTemplates[parseInt(formData.achievementLevels)] || levelTemplates[7])?.map((level, index) => {
                          const totalPoints = parseInt(formData.totalPoints) || 100;
                          const passingThreshold = parseInt(formData.passingThreshold) || 55;
                          const points = Math.floor(totalPoints * level.multiplier);
                          const percentage = Math.floor(level.multiplier * 100);
                          const isPassingLevel = percentage >= passingThreshold;
                          return (
                            <div key={level.level} className={`flex justify-between items-center p-3 rounded border ${isPassingLevel ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                              <div>
                                <span className="font-medium">{index + 1}. {level.name}</span>
                                <span className={`ml-2 px-2 py-1 text-xs font-medium rounded-full ${isPassingLevel ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{isPassingLevel ? 'PASSING' : 'FAILING'}</span>
                              </div>
                              <div className="text-sm text-gray-600 text-right">
                                <div>{points} pts ({percentage}%)</div>
                                <div className="text-xs">{level.multiplier}x multiplier</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <p className="text-sm text-gray-600 mt-3">This {formData.achievementLevels}-level system will be used in your generated rubric.<br /><strong>Passing threshold: {formData.passingThreshold || '55'}%</strong> - Levels at or above this threshold represent passing work.</p>
                    </div>
                  </div>
                </div>

                <div className="mt-8 text-center">
                  <button onClick={generatePrompt} disabled={!isFormValid} className={`px-12 py-4 rounded-xl font-bold text-lg flex items-center gap-4 mx-auto transition-all duration-300 ${isFormValid ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-1' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}>
                    <Sparkles className="w-6 h-6" />
                    Generate AI Prompt
                    <ArrowRight className="w-6 h-6" />
                  </button>
                  {!isFormValid && (
                    <p className="text-sm text-red-600 mt-4">Please fill in all required fields (*) and add at least one learning objective to generate the prompt</p>
                  )}
                </div>
              </>
            ) : (
              <div className="space-y-6">
                <div className="bg-gray-800 text-green-400 p-4 rounded-lg font-mono text-sm max-h-96 overflow-y-auto">
                  <pre className="whitespace-pre-wrap">{generatedPrompt}</pre>
                </div>
                <div className="flex gap-4 justify-center">
                  <button onClick={() => copyToClipboard(generatedPrompt)} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Copy to Clipboard
                  </button>
                  <button onClick={exportPrompt} className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2">
                    <Download className="w-4 h-4" />
                    Download as File
                  </button>
                  <button onClick={() => setShowPrompt(false)} className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center gap-2">
                    <ArrowRight className="w-4 h-4 rotate-180" />
                    Go back to Rubric Generator
                  </button>
                </div>
                <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                  <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2"><Lightbulb className="w-4 h-4" />Next Steps</h4>
                  <ol className="text-blue-700 space-y-1 text-sm">
                    <li>1. Copy the prompt above</li>
                    <li>2. Paste it into your preferred AI tool (ChatGPT, Claude, etc.)</li>
                    <li>3. The AI will generate a complete JSON file</li>
                    <li>4. Save the JSON file and import it into the Rubric Builder</li>
                    <li>5. Customize and refine the rubric as needed</li>
                  </ol>
                </div>
              </div>
            )}
          </div>
        </div>

        {inlineEditor.show && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
              <div className="flex justify-between items-center p-6 border-b">
                <h4 className="text-xl font-semibold text-gray-800">{inlineEditor.field}</h4>
                <div className="flex gap-3">
                  <button onClick={() => closeInlineEditor(true)} className="px-6 py-3 bg-blue-600 text-white rounded-lg flex items-center gap-2 text-sm font-medium">
                    <Save size={18} />
                    Save & Close
                  </button>
                  <button onClick={() => closeInlineEditor(false)} className="px-6 py-3 bg-red-600 text-white rounded-lg flex items-center gap-2 text-sm font-medium">
                    <X size={18} />
                    Cancel
                  </button>
                </div>
              </div>
              <div className="p-6 flex-grow overflow-y-auto">
                <TiptapEditor
                  ref={editorRef}
                  value={inlineEditor.content}
                  onChange={(html) => { richTextContentRef.current = html; }}
                  placeholder={`Enter details for ${inlineEditor.field}...`}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIRubricPromptGenerator;